import { keepPreviousData, useQuery } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from 'src/components/Autocomplete';
import { Avatar } from 'src/components/Avatar';
import { CircularProgress } from 'src/components/CircularProgress';
import { ListItem } from 'src/components/ListItem';
import { TextField } from 'src/components/TextField';
import { searchClients, searchUserClients } from 'src/models/user/user';

import type { ClientSearchResult } from 'src/models/user/_types';

interface ClientAutocompleteProps {
  value?: ClientSearchResult | ClientSearchResult[] | null;
  onChange: (client: ClientSearchResult | ClientSearchResult[] | null) => void;
  label?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  emptySearchOptions?: ClientSearchResult[];
  filterOptions?: (options: ClientSearchResult[]) => ClientSearchResult[];
  filterSelectedOptions?: boolean;
  multiple?: boolean;
  openOnFocus?: boolean;
  searchTypes?: string[];
  placeholder?: string;
  secondary?: (option: ClientSearchResult) => string;
  includeUserSearch?: boolean;
  isOptionEqualToValue?: (
    option: ClientSearchResult,
    value: ClientSearchResult
  ) => boolean;
  resetSearchOnChange?: boolean;
  searchBy?: 'email' | 'phone_number' | 'name';
  trainersOnly?: boolean;
  /** When true, merges staff trainer matches into client search results (e.g. task Regarding). */
  alsoIncludeTrainers?: boolean;
}

const compareOptionIds = (
  a: ClientSearchResult['id'],
  b: ClientSearchResult['id']
) => String(a) === String(b);

export const ClientAutocomplete: React.FC<ClientAutocompleteProps> = ({
  value,
  onChange,
  label,
  autoFocus,
  disabled,
  emptySearchOptions,
  filterOptions,
  filterSelectedOptions,
  multiple,
  openOnFocus,
  searchTypes = ['clients'],
  trainersOnly = false,
  placeholder,
  secondary,
  includeUserSearch = false,
  isOptionEqualToValue,
  resetSearchOnChange = false,
  searchBy,
  alsoIncludeTrainers = false,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'clientAutocomplete' });
  const [searchValue, setSearchValue] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<ClientSearchResult[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (multiple) return;

    const selected = value && !Array.isArray(value) ? value : null;
    if (selected) {
      setInputValue(selected.name || selected.email || '');
      return;
    }

    setInputValue('');
  }, [multiple, value]);

  useEffect(() => {
    if (autoFocus) {
      setTimeout(() => {
        const input = containerRef.current?.querySelector(
          '.MuiAutocomplete-input'
        ) as HTMLElement;
        input?.focus();
      }, 100);
    }
  }, [autoFocus]);

  const {
    data: clients,
    refetch: refetchClients,
    isFetching: searchFetching,
  } = useQuery({
    queryKey: ['users', 'kiosk_search', searchValue, trainersOnly, alsoIncludeTrainers],
    queryFn: () =>
      includeUserSearch
        ? searchUserClients({
            search: searchValue,
            types: searchTypes,
            searchBy,
          })
        : searchClients({
            search: searchValue,
            types: searchTypes,
            trainersOnly,
          }),
    enabled: false,
    placeholderData: keepPreviousData,
    staleTime: 5000,
  });

  const { data: trainerMatches, isFetching: trainerSearchFetching } = useQuery({
    queryKey: ['users', 'regarding_trainer_search', searchValue],
    queryFn: () =>
      searchClients({
        search: searchValue,
        types: searchTypes,
        trainersOnly: true,
      }),
    enabled: alsoIncludeTrainers && searchValue.length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 5000,
  });

  useEffect(() => {
    const memberOptions = clients || [];
    if (!alsoIncludeTrainers) {
      setOptions(memberOptions);
      return;
    }

    const staffOptions = (trainerMatches || []).map((option) => ({
      ...option,
      tagString: 'staff_regarding',
    }));

    const memberIds = new Set(memberOptions.map((option) => option.id));
    const dedupedStaff = staffOptions.filter((option) => !memberIds.has(option.id));

    setOptions([...memberOptions, ...dedupedStaff]);
  }, [alsoIncludeTrainers, clients, trainerMatches]);

  const debouncedSearch = debounce((value: string) => {
    if (value.length >= 2) {
      refetchClients();
    }
  }, 300);

  const autocompleteOptions = useMemo(() => {
    if (multiple) {
      const selected = Array.isArray(value) ? value : [];
      const missing = selected.filter(
        (selectedOption) =>
          !options.some((option) =>
            compareOptionIds(option.id, selectedOption.id)
          )
      );
      return missing.length ? [...missing, ...options] : options;
    }

    const selected = value && !Array.isArray(value) ? value : null;
    if (!selected) return options;
    if (options.some((option) => compareOptionIds(option.id, selected.id)))
      return options;

    return [selected, ...options];
  }, [multiple, options, value]);

  const handleInputChange = (
    _: React.SyntheticEvent,
    newInput: string,
    reason: string
  ) => {
    if (reason === 'input') {
      setInputValue(newInput);
      setSearchValue(newInput);
      debouncedSearch(newInput);
      return;
    }

    if (reason === 'clear') {
      setInputValue('');
      setSearchValue('');
    }
  };

  const getOptionLabel = (option: ClientSearchResult) =>
    option.name || option.email || '';

  return (
    <div ref={containerRef}>
      <Autocomplete
        disabled={disabled}
        filterSelectedOptions={filterSelectedOptions}
        multiple={multiple}
        openOnFocus={openOnFocus}
        options={
          emptySearchOptions !== undefined && !searchValue
            ? emptySearchOptions
            : autocompleteOptions
        }
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={
          isOptionEqualToValue ??
          ((option, selected) => compareOptionIds(option.id, selected.id))
        }
        inputValue={multiple ? undefined : inputValue}
        value={value || (multiple ? [] : null)}
        onChange={(_, newValue) => {
          onChange(multiple ? (newValue as ClientSearchResult[]) : newValue);
          if (resetSearchOnChange) {
            setSearchValue('');
            setOptions([]);
          }
        }}
        onInputChange={handleInputChange}
        loading={searchFetching || trainerSearchFetching}
        filterOptions={(x) => (filterOptions ? filterOptions(x) : x)}
        noOptionsText={
          searchValue ? t('noAccountsFound') : t('startTypingToFindAccounts')
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label ?? t('selectClient')}
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {searchFetching || trainerSearchFetching ? (
                    <CircularProgress color="inherit" size="small" />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <ListItem
            {...(props as any)}
            key={option.id}
            clickable
            primary={option.name}
            secondary={secondary ? secondary(option) : option.email}
            avatar={<Avatar src={option.profileImage} />}
          />
        )}
      />
    </div>
  );
};
